import React, { useState } from 'react';
import { useQuestions } from '../../Context/QuestionContext';
import { useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Check } from 'lucide-react';
import { Form, Button, Card, Row, Col, InputGroup } from 'react-bootstrap';

const CreateQuestion = () => {
  const { addQuestion } = useQuestions();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    text: '',
    type: 'MULTIPLE_CHOICE',
    difficulty: 'EASY',
    correctAnswers: [], 
    options: ['', '', '', ''],
    timeLimit: 20,
    mediaUrl: '',
    mediaType: 'IMAGE'
  });

  const [file, setFile] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.text || formData.correctAnswers.length === 0) return;
    
    // Map indices back to values for MULTIPLE_CHOICE, or use directly for CLASSIC
    let correctAnswerValues;
    if (formData.type === 'MULTIPLE_CHOICE') {
        correctAnswerValues = formData.correctAnswers.map(idx => formData.options[idx]).filter(val => val !== '');
    } else {
        correctAnswerValues = formData.correctAnswers;
    }
    
    if (correctAnswerValues.length === 0) return;

    const data = new FormData();
    data.append('text', formData.text);
    data.append('type', formData.type);
    data.append('difficulty', formData.difficulty);
    data.append('timeLimit', formData.timeLimit);
    // For CLASSIC, options should be the answer value
    const optionsPayload = formData.type === 'CLASSIC' ? correctAnswerValues : formData.options;
    data.append('options', JSON.stringify(optionsPayload));
    data.append('correctAnswers', JSON.stringify(correctAnswerValues));
    
    if (file) {
        data.append('media', file); // Use 'media' key as expected by multer
        data.append('mediaType', 'IMAGE');
    } else if (formData.mediaUrl) {
        data.append('mediaUrl', formData.mediaUrl);
        data.append('mediaType', formData.mediaType);
    }
    
    await addQuestion(data);
    navigate('/questions');
  };

  const updateOption = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };

  const toggleCorrectAnswer = (index) => {
      const current = formData.correctAnswers || [];
      const idxPos = current.indexOf(index);
      let newAnswers;
      if (idxPos > -1) {
          newAnswers = current.filter(i => i !== index);
      } else {
          newAnswers = [...current, index];
      }
      setFormData({ ...formData, correctAnswers: newAnswers });
  };

  return (
    <div className="py-2 mx-auto" style={{maxWidth: '800px'}}>
      <Button 
        variant="link" 
        onClick={() => navigate('/questions')}
        className="text-decoration-none text-muted mb-3 p-0 d-flex align-items-center"
      >
        <ArrowLeft size={18} className="me-2" />
        Soru Listesine Dön
      </Button>

      <Card className="custom-card">
        <Card.Body className="p-4 p-md-5">
          <h3 className="fw-bold mb-4 text-gradient">Yeni Soru Oluştur</h3>

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-4">
              <Form.Label className="text-secondary fw-medium small">SORU METNİ</Form.Label>
              <Form.Control
                size="lg"
                type="text"
                placeholder="Örn: Işık hızı kaçtır?"
                value={formData.text}
                onChange={(e) => setFormData({...formData, text: e.target.value})}
                required
                className="fw-medium shadow-none"
              />
            </Form.Group>

            <Row className="mb-4">
              <Col md={6}>
                 <Form.Label className="text-secondary fw-medium small">ZORLUK SEVİYESİ</Form.Label>
                 <div className="d-flex bg-light p-1 rounded">
                    {[
                      { id: 'EASY', label: 'Basit', variant: 'success' },
                      { id: 'MEDIUM', label: 'Orta', variant: 'warning' },
                      { id: 'HARD', label: 'Zor', variant: 'danger' }
                    ].map((level) => (
                      <Button
                        key={level.id}
                        variant={formData.difficulty === level.id ? 'white' : 'transparent'}
                        className={`flex-fill fw-bold small border-0 ${formData.difficulty === level.id ? 'shadow-sm text-' + level.variant + ' bg-white' : 'text-muted'}`}
                        onClick={() => setFormData({...formData, difficulty: level.id})}
                      >
                        {level.label}
                      </Button>
                    ))}
                 </div>
              </Col>
              
              <Col md={6}>
                 <Form.Label className="text-secondary fw-medium small">SORU TİPİ</Form.Label>
                 <Form.Select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="shadow-none"
                 >
                    <option value="MULTIPLE_CHOICE">Çoktan Seçmeli</option>
                    <option value="CLASSIC">Klasik (Yazılı Cevap)</option>
                 </Form.Select>
              </Col>
            </Row>

            {formData.type === 'MULTIPLE_CHOICE' && (
              <div className="mb-4">
                 <Form.Label className="text-secondary fw-medium small">SEÇENEKLER {formData.correctAnswers.length > 1 && <span className="text-primary small">(Birden fazla doğru cevap seçildi)</span>}</Form.Label>
                 <Row className="g-3">
                    {formData.options.map((opt, idx) => (
                       <Col md={6} key={idx}>
                          <InputGroup>
                             <InputGroup.Text className="fw-bold bg-light text-muted border-end-0" style={{width: 40, justifyContent: 'center'}}>
                                {['A', 'B', 'C', 'D'][idx]}
                             </InputGroup.Text>
                             <Form.Control
                                type="text"
                                placeholder={`Seçenek ${idx + 1}`}
                                value={opt}
                                onChange={(e) => updateOption(idx, e.target.value)}
                                className={`border-start-0 shadow-none ${formData.correctAnswers.includes(idx) ? 'border-success bg-success bg-opacity-10' : ''}`}
                                required
                             />
                             <Button 
                                variant={formData.correctAnswers.includes(idx) ? 'success' : 'light'}
                                onClick={() => toggleCorrectAnswer(idx)}
                                title="Doğru cevap olarak işaretle"
                                className="border"
                             >
                                <Check size={18} />
                             </Button>
                          </InputGroup>
                       </Col>
                    ))}
                 </Row>
                 <Form.Text className="text-muted d-block mt-2 small">
                    {formData.correctAnswers.length === 0 ? <span className="text-danger">* En az bir doğru cevap işaretleyiniz.</span> : '* Çoklu seçim için birden fazla şıkkı işaretleyebilirsiniz.'}
                 </Form.Text>
              </div>
            )}

            {formData.type === 'CLASSIC' && (
               <Form.Group className="mb-4">
                  <Form.Label className="text-secondary fw-medium small">DOĞRU CEVAP (Sadece Sayı)</Form.Label>
                  <Form.Control
                     type="number"
                     step="any"
                     placeholder="Örn: 42 veya 3.14"
                     value={formData.correctAnswers[0] || ''}
                     onChange={(e) => setFormData({...formData, correctAnswers: [e.target.value]})}
                     className="border-success shadow-none"
                     required
                  />
               </Form.Group>
            )}

            <Form.Group className="mb-4">
               <Form.Label className="text-secondary fw-medium small">SÜRE LİMİTİ (SN)</Form.Label>
               <Form.Control
                  type="number"
                  style={{maxWidth: 150}}
                  value={formData.timeLimit}
                  onChange={(e) => setFormData({...formData, timeLimit: parseInt(e.target.value)})}
                  min={5}
                  max={300}
                  className="shadow-none"
               />
            </Form.Group>

            <Row className="mb-4">
              <Col md={12}>
                 <Form.Label className="text-secondary fw-medium small">MEDYA (OPSİYONEL)</Form.Label>
                 <InputGroup className="mb-2">
                    <Form.Control
                       type="file"
                       accept="image/*"
                       onChange={(e) => {
                           if (e.target.files[0]) {
                               setFile(e.target.files[0]);
                               setFormData({...formData, mediaUrl: URL.createObjectURL(e.target.files[0]), mediaType: 'IMAGE'});
                           }
                       }}
                       className="shadow-none"
                    />
                 </InputGroup>
                 <div className="text-muted small mb-2">Veya URL girin:</div>
                 <InputGroup className="mb-2">
                    <Form.Control
                       type="url"
                       placeholder="https://..."
                       value={file ? '' : formData.mediaUrl}
                       onChange={(e) => {
                           setFile(null);
                           setFormData({...formData, mediaUrl: e.target.value, mediaType: 'IMAGE'});
                       }}
                       disabled={!!file}
                       className="shadow-none"
                    />
                 </InputGroup>

                 {(formData.mediaUrl || file) && (
                    <div className="mt-2 rounded overflow-hidden border bg-light d-flex align-items-center justify-content-center" style={{height: 200}}>
                        <img src={formData.mediaUrl} alt="Preview" className="h-100 object-fit-contain" onError={(e) => e.target.style.display='none'} />
                    </div>
                 )}
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-3 pt-3 border-top">
               <Button variant="light" onClick={() => navigate('/questions')} className="px-4 border-0">
                  İptal
               </Button>
               <Button type="submit" variant="primary" className="px-5 shadow-sm d-flex align-items-center gap-2 border-0">
                  <Save size={18} />
                  Kaydet
               </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default CreateQuestion;
